import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {equipmentListToLabel, errorMsg} from "../../../../../Apis/functions";
import HandleChange from "../../../../../Helpers/HandleChange";
import {CHECK_IN_MODEL} from "../Model/Model";
import {toast} from "react-toastify";
import {useDispatch, useSelector} from "react-redux";
import {
    EquipmentCheckInOut,
    GetAccessLogEntryById
} from "../../../../../reduxToolkit/EquipmentAndMaterial/EquipmentApi";
import {ACCESS_TYPE_MATERIAL_MODEL} from "../../../Contractors/Model/Models";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 03 de diciembre de 2025
 *
 * Check-out modal logic controller hook.
 *
 * This hook handles the state and logic required for the check-out modal,
 * including equipment list selections, form validation, checkbox tracking,
 * and dynamic model transformations.
 *
 * NOTE: All API calls inside this module are commented out and replaced with
 * dummy front-end placeholder logic.
 *
 * @param {Object} props
 * @param {string|number} props.itemSelected - Optional ID used to fetch data from API (dummy in this version).
 * @returns {Object} Exposes modal state and handlers for UI components.
 */
export const CheckOutModalApp = ({itemSelected, onHide}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.authenticatioauthennSlice);
    const dataUser = user?.data;

    const [item, setItem] = useState(CHECK_IN_MODEL.set({
            userId: itemSelected?.userId,
            guardId: dataUser?.id,
            guardFullName: dataUser?.fullName,
            accessType: ACCESS_TYPE_MATERIAL_MODEL.EXIT.name
        })); //CHECK_OUT_MODEL

    const [newSelected, setNewSelected] = useState(null);
    const [selectedList, setSelectedList] = useState([]); //EQUIPMENT_MODEL
    const [showBadgeMessage, setBadgeMessage] = useState(true);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [checkedIds, setCheckedIds] = useState([]);

    /**
     * Closes the informational badge.
     *
     * @function handleCloseBadge
     */
    const handleCloseBadge = () => {
        setBadgeMessage(false);
    }

    useEffect(() => {
        if (itemSelected?.equipmentAccessLogId) {
            dispatch(GetAccessLogEntryById(itemSelected?.equipmentAccessLogId))
                .then(({ payload: { data:{data, success, code} } }) => {
                    if (success) {
                        if(data?.equipmentList?.length > 0)
                            setSelectedList(equipmentListToLabel(data?.equipmentList?.map(e => ({...e, saved:true})), 'id', true));
                    } else {
                        errorMsg(code);
                    }
                });
        }
    }, [itemSelected]);

    /**
     * Adds or removes the ID of a checked equipment item.
     *
     * @param {string} id - Equipment identifier.
     */
    const handleCheckboxChange = id => {
        setCheckedIds(prev =>
            prev.includes(id)
                ? prev.filter(itemId => itemId !== id)
                : [...prev, id]
        );
    }

    /**
     * Handles form submission and validates fields.
     * Uses dummy behavior for non-implemented API.
     *
     * @function handleCreate
     */
    const handleCheckOut = () => {
        const body = item.toRegister(checkedIds.map(e => ({id:e})));

        dispatch(EquipmentCheckInOut(body)).then(({payload:{data:{success, code}}})=>{
            if (success){
                toast.success(t('has_been_created_successfully'));
                onHide();
            }else
                errorMsg(code);
        });
    };

    const handleSelected = element => {
        setSelectedList(prev => [...prev, {...element, saved:false}]);
        setNewSelected(null);
        setCheckedIds(prev => [...prev, element.id]);
    };

    const deleteSelected = (id) => {
        setSelectedList(prev => prev.filter(e => e.id !== id));
    };

    const removeSelectedIds = (list) => {
        return  list.filter(e => !selectedList.map(s => s.id)?.includes(e.id))
    };

    return {
        showBadgeMessage,
        handleCloseBadge,
        t,
        selectedList,
        item,
        handleChange,
        handleCheckboxChange,
        checkedIds,
        newSelected,
        setNewSelected,
        handleSelected,
        handleCheckOut,
        deleteSelected,
        removeSelectedIds
    };

};
